import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, RotateCcw, Loader2, Sparkles, Check, Pencil, ClipboardList } from "lucide-react";
import { usePestTranslation } from "@/hooks/usePestTranslation";
import { useFociEnabled } from "@/hooks/useFociEnabled";
import { useSessionPlace } from "@/hooks/useSessionPlace";
import { useConfirmSamplingForm } from "@/hooks/useConfirmSamplingForm";
import { useConfirmScoutingSession } from "@/hooks/useConfirmScoutingSession";
import { usePestFocusAction } from "@/hooks/usePestFocusAction";
import { focusErrorOf } from "@/lib/pests/confirmErrors";
import { ConfirmSamplingFields } from "@/components/pests/ConfirmSamplingFields";
import { AiSuggestionChip } from "@/components/pests/AiSuggestionChip";
import { usePestCropAiEnabled } from "@/hooks/usePestCropAiEnabled";
import { useAiSuggestion, type AiSuggestion } from "@/hooks/useAiSuggestion";
import { SHEET_CENTERED_MAX_H_CLASS } from "@/components/ui/sheetChrome";
import { cn } from "@/lib/utils";

/**
 * LOS BOTONES DE ESTE PIE SE HACEN COMO LOS DE «FILTROS».
 *
 * Eran los `Button` de serie a 44 px: radio pequeño, sombra de sistema y la
 * tipografía del cuerpo. El pie de «Filtros» —«Limpiar» / «Aplicar»— es el que
 * marca cómo se cierra una hoja en esta app: 52 px de alto, 14 de radio,
 * Poppins en 700 y una sombra en el color del propio botón.
 *
 * Los colores NO cambian: naranja de plagas para la clasificación, azul para el
 * tratamiento, verde para resuelto y rojo para descartar. Lo que cambia es la
 * forma, para que el pie de la ficha de sesión y el de los filtros se lean como
 * la misma app.
 */
export const ACTION_CLASS =
  "w-full flex items-center justify-center gap-2 font-['Poppins'] text-white transition-transform " +
  "active:scale-[0.99] hover:brightness-95 disabled:opacity-50 disabled:active:scale-100";

export const actionStyle = (background: string, glow: string): React.CSSProperties => ({
  height: 52,
  borderRadius: 14,
  background,
  fontWeight: 700,
  fontSize: 16,
  boxShadow: `0 8px 18px ${glow}`,
});

/**
 * UN 20 % MÁS PEQUEÑOS, SOLO EN ORDENADOR (usuario 16-10-2026, chat de edición
 * visual: «These 20% smaller, please. Only on computer»). Son los tres botones
 * del parte ya clasificado —la clasificación, «Tratamiento» y «Resuelto»—, que
 * en pantalla grande ocupaban más de lo que piden.
 *
 * Va con el modificador `!` a propósito: el tamaño de estos botones vive en
 * `actionStyle` como estilo EN LÍNEA (alto 52, letra 16, radio 14) y un estilo
 * en línea le gana a cualquier clase por específica que sea. 52→42, 16→13 y
 * 14→11 es el 20 % redondeado al píxel. En móvil y tableta no cambia nada.
 */
const ACCION_COMPACTA_LG = "lg:!h-[42px] lg:!text-[13px] lg:!rounded-[11px]";
const ICONO_COMPACTO_LG = "lg:w-[14px] lg:h-[14px]";

/** «Limpiar»: blanco con borde, para lo que no es la acción principal. */
const QUIET_ACTION_CLASS =
  "flex items-center justify-center gap-2 font-['Poppins'] transition-transform active:scale-95 disabled:opacity-50";
const QUIET_ACTION_STYLE: React.CSSProperties = {
  height: 52,
  padding: "0 18px",
  borderRadius: 14,
  background: "#fff",
  border: "1px solid #E4EDF5",
  color: "#2E4A63",
  fontWeight: 700,
  fontSize: 16,
};

export const ACTION_COLORS = {
  pest: { bg: "#E0962E", glow: "rgba(224,150,46,0.30)" },
  blue: { bg: "#2E84DE", glow: "rgba(46,132,222,0.30)" },
  green: { bg: "#059669", glow: "rgba(5,150,105,0.30)" },
  red: { bg: "#E5574E", glow: "rgba(229,87,78,0.30)" },
} as const;

interface TriageState {
  triage_status: "pending_triage" | "classified" | "dismissed" | "not_required";
  triage_pest_id: string | null;
  triage_pest_name_snapshot: string | null;
  dismissal_reason: string | null;
  dismissal_note: string | null;
  worker_resolved_at?: string | null;
  review_status?: string | null;
  linked_task_id?: string | null;
}

interface Props {
  sessionId: string;
  state: TriageState;
  onChanged: () => void;
  onCreateTreatment?: () => void;
  /** El `ai_analysis` del parte, que la ficha ya tiene leído: respaldo de la sugerencia de la IA (F7). */
  aiAnalysis?: unknown;
}

const REASONS = ["no_pest", "duplicate", "not_actionable"] as const;

/** La base no ha dejado cambiar el parte (0 filas): casi siempre, permisos. */
const NO_ROWS_UPDATED = "NO_ROWS_UPDATED";

export function ScoutingTriagePanel({ sessionId, state, onChanged, onCreateTreatment, aiAnalysis = null }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pestTranslation = usePestTranslation();

  const [pests, setPests] = useState<Array<{ id: string; name: string; translation_key?: string | null }>>([]);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  // Quitar la clasificación borra la plaga asignada, así que siempre se confirma.
  const [unclassifyOpen, setUnclassifyOpen] = useState(false);
  /* Una sola pregunta, usada por el desplegable y por el pie del diálogo. */
  const yaClasificado = state.triage_status === "classified";
  const [selectedPestId, setSelectedPestId] = useState<string>("");
  const [reason, setReason] = useState<string>("no_pest");
  const [busy, setBusy] = useState(false);

  /* CON FOCOS, clasificar es CONFIRMAR: plaga + gravedad obligatoria + a qué foco
     pertenece, todo de una vez y por una sola puerta (`useConfirmScoutingSession`).
     Sin focos —empresa fuera del piloto, base sin migrar o el parte no se pudo
     leer— este diálogo es exactamente el de antes: `fociOff` lo decide, y también
     se enciende si la base contesta «aquí no hay focos» al confirmar. */
  const { enabled: fociEnabled } = useFociEnabled();
  const [fociOff, setFociOff] = useState(false);
  /* Se lee al abrir la ficha de un parte SIN confirmar (no solo al abrir el
     diálogo): si el trabajador contestó «ya no hay», eso se ofrece antes que nada. */
  const porConfirmar = !yaClasificado && state.triage_status !== "dismissed" && state.review_status !== "resolved";
  const sessionPlace = useSessionPlace(sessionId, fociEnabled && !fociOff && porConfirmar);
  const focusAction = usePestFocusAction();
  const withFoci = fociEnabled && !fociOff && !yaClasificado && !sessionPlace.failed;
  /* El trabajador dijo que es LO MISMO que un sitio conocido (o vino a revisarlo por una
     tarea): la plaga de ese sitio viene ya elegida y quien confirma solo pone la gravedad.
     Se puede cambiar; y si dijo «es otra cosa» o «no lo sé», no se elige nada por él. */
  useEffect(() => {
    const hf = sessionPlace.data?.hintFocus;
    const hint = sessionPlace.data?.place.hint;
    if (!withFoci || !porConfirmar || selectedPestId) return;
    if (hf?.pestId && hint && (hint.answer === "same" || hint.fromTask)) {
      setSelectedPestId(hf.pestId);
      return;
    }
    // Sin sitio conocido: la plaga que ENTENDIÓ LA VOZ del trabajador (D28), también cambiable.
    const vg = sessionPlace.data?.voiceGuess;
    if (vg?.pestId) setSelectedPestId(vg.pestId);
  }, [withFoci, porConfirmar, selectedPestId, sessionPlace.data]);
  const pestFromVoice = !!withFoci && !!selectedPestId && sessionPlace.data?.voiceGuess?.pestId === selectedPestId;
  const selectedPest = pests.find((p) => p.id === selectedPestId) ?? null;

  /* LA SUGERENCIA DE LA IA (plan cultivos SIEX F7, 05-10-2026): con la IA guiada por
     cultivo, el servidor dice QUÉ plaga de la empresa ve en la foto (`ai_pest_id`). Va
     como PASTILLA etiquetada como de la IA (F6), por debajo de lo que dijo una persona —la
     pista «es lo mismo» y la voz del trabajador preseleccionan; esto no—, y elegirla es lo
     mismo que elegirla en el desplegable: el foco lo propone la regla de siempre (F1).
     Solo con el interruptor `pest_crop_ai`; apagado, el diálogo es el de antes. */
  const { on: cropAiOn } = usePestCropAiEnabled();
  const aiSuggestion = useAiSuggestion(sessionId, cropAiOn && porConfirmar, aiAnalysis);
  const aiPest = aiSuggestion.data ? pests.find((p) => p.id === aiSuggestion.data!.pestId) ?? null : null;
  const aiChip: (AiSuggestion & { name: string }) | null =
    aiSuggestion.data && aiPest && !yaClasificado ? { ...aiSuggestion.data, name: pestTranslation.getName(aiPest) } : null;

  /** Para medir el anclaje (F8): qué sugirió la IA, que se enseñó y qué eligió la persona. Nunca bloquea. */
  const registrarSugerencia = async (chosenPestId: string | null) => {
    if (!aiChip || !company?.id) return;
    try {
      const sb = supabase as unknown as { from: (t: string) => { insert: (row: Record<string, unknown>) => Promise<{ error: { message?: string } | null }> } };
      const { error } = await sb.from("ai_suggestion_events").insert({
        company_id: company.id,
        session_id: sessionId,
        analysis_id: aiChip.analysisId,
        suggested_pest_id: aiChip.pestId,
        suggested_outcome: aiChip.aiOutcome,
        suggested_confidence: aiChip.confidence,
        shown: true,
        accepted: chosenPestId === aiChip.pestId,
        chosen_pest_id: chosenPestId,
        surface: "triage",
        candidate_mode: aiChip.candidateMode,
      });
      if (error) console.warn("ai_suggestion_events no guardado:", error.message);
    } catch (e) {
      console.warn("ai_suggestion_events no guardado:", e);
    }
  };
  const form = useConfirmSamplingForm({
    enabled: withFoci && classifyOpen && !!sessionPlace.data,
    place: sessionPlace.data?.place ?? null,
    pestId: selectedPestId || null,
    pestName: selectedPest ? pestTranslation.getName(selectedPest) : null,
    initialSeverity: sessionPlace.data?.severity ?? null,
    aiSeverity: sessionPlace.data?.aiSeverity ?? null,
    initialVerdict: sessionPlace.data?.verdict ?? null,
  });
  const { confirm } = useConfirmScoutingSession();

  useEffect(() => {
    if (!classifyOpen || pests.length > 0) return;
    supabase
      .from("pests")
      .select("id, name, translation_key")
      .order("name")
      .then(({ data }) => setPests(data || []));
  }, [classifyOpen, pests.length]);

  const handleConfirmWithFocus = async () => {
    const input = form.buildInput(sessionId);
    if (!input) return;
    setBusy(true);
    try {
      const out = await confirm(input);
      if (out.ok) {
        void registrarSugerencia(selectedPestId || null);
        const focus = out.result.focusNumber != null ? t("healthMap.focus.number", { n: out.result.focusNumber }) : "";
        toast({
          title: t("pests.scouting.triage.saved"),
          description: out.result.reopened
            ? t("pests.scouting.focusLink.reopened")
            : out.result.joinedClosed
              ? t("pests.scouting.focusLink.closedFocus.joined", { focus })
              : out.result.createdFocus
              ? t("pests.scouting.focusLink.createdFocus")
              : out.result.focusId
                ? t("pests.scouting.focusLink.linked", { focus })
                : undefined,
        });
        if (out.result.inheritedTaskId) toast({ title: t("pests.scouting.focusLink.inheritsTask") });
        setClassifyOpen(false);
        setSelectedPestId("");
        onChanged();
        return;
      }
      if (out.error.kind === "legacy") {
        // Aquí no hay focos (empresa fuera del piloto o base sin migrar): el camino de siempre.
        setFociOff(true);
        await handleClassify();
        return;
      }
      // «Lo que veías ya no es verdad»: se vuelven a pedir los focos y el diálogo sigue abierto.
      if (out.error.kind === "refetch") form.refetch();
      toast({ title: t(out.error.messageKey), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  /* Toda escritura del panel sobre el parte pasa por aquí y COMPRUEBA que ha
     cambiado algo. Sin `.select()`, una regla de la base que no deja tocar la
     fila devuelve 200 con 0 filas y la pantalla decía «guardado» sin haber
     hecho nada (auditoría del perito: PREP-03, T06-08). */
  const updateSession = async (patch: Record<string, unknown>) => {
    const { data, error } = await supabase
      .from("pest_scouting_sessions")
      .update(patch as never)
      .eq("id", sessionId)
      .select("id");
    if (error) throw error;
    if (!data || data.length === 0) throw new Error(NO_ROWS_UPDATED);
  };
  const saveErrorTitle = (e: unknown) =>
    e instanceof Error && e.message === NO_ROWS_UPDATED
      ? t("pests.scouting.triage.noPermission")
      : t("pests.scouting.triage.saveError");

  const handleClassify = async () => {
    if (!selectedPestId) return;
    setBusy(true);
    try {
      const pest = pests.find((p) => p.id === selectedPestId);
      const pestName = pest ? pestTranslation.getName(pest) : null;
      await updateSession({
          triage_status: "classified",
          triage_pest_id: selectedPestId,
          triage_pest_name_snapshot: pestName,
          dismissal_reason: null,
          dismissal_note: null,
          triaged_by: user?.id,
          triaged_at: new Date().toISOString(),
        });
      toast({ title: t("pests.scouting.triage.saved") });
      void registrarSugerencia(selectedPestId);
      setClassifyOpen(false);
      setSelectedPestId("");
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await updateSession({
          triage_status: "dismissed",
          dismissal_reason: reason,
          triaged_by: user?.id,
          triaged_at: new Date().toISOString(),
        });
      toast({ title: t("pests.scouting.triage.dismissed") });
      setDismissOpen(false);
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    try {
      await updateSession({
          triage_status: "pending_triage",
          review_status: "pending",
          triage_pest_id: null,
          triage_pest_name_snapshot: null,
          dismissal_reason: null,
          dismissal_note: null,
          worker_resolved_at: null,
          triaged_by: null,
          triaged_at: null,
        });
      toast({ title: t("pests.scouting.triage.reopened") });
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  /** Confirmado el aviso: reutiliza handleReopen, que ya deja la sesión como si nunca
   *  se hubiera clasificado, y cierra el aviso y el diálogo del lápiz. */
  const handleUnclassify = async () => {
    await handleReopen();
    setUnclassifyOpen(false);
    setClassifyOpen(false);
  };

  /* «Reabrir» un parte RESUELTO. Dentro de un foco significa «la plaga ha vuelto»: se
     reabre, NO se desclasifica (antes lo devolvía a «pendiente de clasificar», lo sacaba
     del foco y dejaba a sus hermanos resueltos — A4 de la revisión del 23-09-2026). Si su
     lote es el último cierre del foco, se reabre el FOCO (queda «Reabierto por …» en la
     historia); si era de un cierre anterior, vuelve a abierto con su lote. Fuera de un
     foco, lo de siempre. */
  const handleReopenResolved = async () => {
    if (fociEnabled && !fociOff) {
      const { data: row } = await supabase
        .from("pest_scouting_sessions")
        .select("focus_id, linked_task_id, resolved_at")
        .eq("id", sessionId)
        .maybeSingle();
      const focusId = (row as { focus_id: string | null } | null)?.focus_id ?? null;
      if (row && focusId) {
        setBusy(true);
        try {
          // El último cierre del foco, como lo mide la base (`max(resolved_at)`): sin los
          // partes antiguos sin sello, que en Postgres irían los primeros.
          const { data: latest } = await supabase
            .from("pest_scouting_sessions")
            .select("resolved_at")
            .eq("focus_id", focusId)
            .eq("triage_status", "classified")
            .eq("review_status", "resolved")
            .not("resolved_at", "is", null)
            .order("resolved_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          const r = row as { linked_task_id: string | null; resolved_at: string | null };
          const reopenOnlyThis = async () => {
            await updateSession({ review_status: r.linked_task_id ? "in_review" : "pending" });
          };
          if (latest?.resolved_at && latest.resolved_at === r.resolved_at) {
            try {
              await focusAction.mutateAsync({ action: "reopen", focus_id: focusId });
            } catch (e) {
              // Reabrir el foco entero es de admin/encargado; quien no lo es (el perito que
              // hizo el parte) reabre su parte, como hasta ahora, y la sincronía su lote.
              // Y si el foco ya NO está cerrado (otro lo reabrió entretanto, o tiene un rebrote
              // abierto), la base ya no reabre otro cierre ni suma un rebrote (auditoría 23-09
              // ciclo#1): se reabre solo el lote de este parte, como con un cierre anterior.
              const code = focusErrorOf(e).code;
              if (code !== "PERMISSION_DENIED" && code !== "FOCUS_NOT_RESOLVED") throw e;
              await reopenOnlyThis();
            }
          } else {
            await reopenOnlyThis();
          }
          toast({ title: t("healthMap.focus.done.reopen") });
          onChanged();
          invalidateScoutingData(queryClient);
        } catch (e) {
          console.error(e);
          toast({ title: t(focusErrorOf(e).messageKey), variant: "destructive" });
        } finally {
          setBusy(false);
        }
        return;
      }
    }
    await handleReopen();
  };

  // Manual "Marcar resuelto": resolution is tracked on review_status (RESUELTO),
  // independent of the pest classification (triage_status).
  const handleResolve = async () => {
    setBusy(true);
    try {
      await updateSession({ review_status: "resolved" });
      toast({ title: t("pests.scouting.triage.resolved") });
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // Manager accepts the worker's "resolved" request: mark the session RESUELTO
  // (review_status='resolved'). Keeps worker_resolved_at as the original trace.
  const handleAcceptWorkerResolution = async () => {
    setBusy(true);
    try {
      await updateSession({
          review_status: "resolved",
          triaged_by: user?.id,
          triaged_at: new Date().toISOString(),
        });
      toast({ title: t("pests.scouting.triage.resolved") });
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // Manager discards the worker's "resolved" request: keep the alert pending,
  // so the manager can still classify or create a treatment task.
  const handleDiscardWorkerResolution = async () => {
    setBusy(true);
    try {
      await updateSession({ worker_resolved_at: null });
      onChanged();
      invalidateScoutingData(queryClient);
    } catch (e) {
      console.error(e);
      toast({ title: saveErrorTitle(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleCreateTreatment = () => {
    if (onCreateTreatment) {
      onCreateTreatment();
      return;
    }
    if (state.triage_pest_id) {
      navigate(`/pests?tab=treatments&pest=${state.triage_pest_id}`);
    } else {
      navigate(`/pests?tab=treatments`);
    }
  };

  // Legacy 'not_required' sessions are actionable too — they fall through to the
  // Classify/Dismiss branch below instead of showing a dead, empty panel.

  // When worker has marked as resolved but manager has NOT yet classified/dismissed,
  // show an explicit Accept / Discard request card.
  const workerResolvedAlert = state.worker_resolved_at && state.triage_status === "pending_triage" && (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 mb-3 space-y-2">
      <div className="flex items-center gap-2 text-emerald-800">
        <CheckCircle2 className="w-[18px] h-[18px] shrink-0" />
        <span className="text-xs font-semibold">{t("worker.incident.markedResolvedBadge")}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          size="sm"
          onClick={handleAcceptWorkerResolution}
          disabled={busy}
          className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Check className="w-3.5 h-3.5 mr-1" />
          {t("pests.scouting.triage.acceptResolution")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDiscardWorkerResolution}
          disabled={busy}
          className="h-9 border-emerald-300 text-emerald-800"
        >
          <XCircle className="w-3.5 h-3.5 mr-1" />
          {t("pests.scouting.triage.discardResolution")}
        </Button>
      </div>
    </div>
  );

  /* «YA NO HAY» — el trabajador volvió a un sitio donde se había avisado de una
     plaga y dice que ya no la ve. Eso no se confirma como plaga: se registra como
     REVISIÓN LIMPIA del foco, que lo cierra y no cuenta como falsa alarma de nadie. */
  const goneFocusId = withFoci && porConfirmar && sessionPlace.data?.workerSaysGone ? sessionPlace.data.place.hint?.focusId ?? null : null;
  const handleCleanCheck = () => {
    if (!goneFocusId) return;
    focusAction.mutate(
      { action: "clean_check", focus_id: goneFocusId, session_id: sessionId, close_focus: true },
      {
        onSuccess: () => { toast({ title: t("healthMap.focus.done.clean_check") }); onChanged(); },
        onError: (e) => toast({ title: t(focusErrorOf(e).messageKey), variant: "destructive" }),
      },
    );
  };
  const workerGoneAlert = goneFocusId && (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 mb-3 space-y-2" data-testid="triage-worker-gone">
      <div className="flex items-center gap-2 text-emerald-800">
        <CheckCircle2 className="w-[18px] h-[18px] shrink-0" />
        <span className="text-xs font-semibold">{t("pests.scouting.focusLink.cleanCheckOffer")}</span>
      </div>
      <Button size="sm" onClick={handleCleanCheck} disabled={busy || focusAction.isPending} className="h-9 w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="triage-clean-check">
        {focusAction.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
        {t("pests.scouting.focusLink.cleanCheckDo")}
      </Button>
    </div>
  );

  let content;
  if (state.review_status === "resolved") {
    content = (
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-600">
        <div className="flex items-center gap-2 text-white text-sm min-w-0">
          <CheckCircle2 className="w-[18px] h-[18px] shrink-0" />
          <span className="font-medium truncate">
            {t("pests.scouting.triage.resolvedSummary")}{state.triage_pest_name_snapshot ? ` · ${state.triage_pest_name_snapshot}` : ""}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleReopenResolved} disabled={busy} className="text-white hover:bg-white/15 hover:text-white" data-testid="triage-reopen-resolved">
          <RotateCcw className="w-3 h-3 mr-1" />
          {t("pests.scouting.triage.reopen")}
        </Button>
      </div>
    );
  } else if (state.triage_status === "classified") {
    content = (
      <div className="space-y-2.5 lg:space-y-2">
        {/* La clasificación es ya un botón, con la misma altura, el mismo radio y el
            mismo grosor que «Tratamiento» y «Resuelto» de abajo, en el naranja de las
            plagas (#E0962E, el del icono de Lista de plagas) y con el texto en blanco.
            Se pulsa entera y hace lo que hacía el lápiz: abrir la reclasificación. */}
        <button
          type="button"
          onClick={() => {
            setSelectedPestId(state.triage_pest_id || "");
            setClassifyOpen(true);
          }}
          data-testid="triage-classified"
          aria-label={t("pests.scouting.triage.editClassification")}
          className={cn(ACTION_CLASS, ACCION_COMPACTA_LG, "!justify-between px-4 lg:px-3")}
          style={actionStyle(ACTION_COLORS.pest.bg, ACTION_COLORS.pest.glow)}
        >
          <span className="truncate text-left">
            {t("pests.scouting.triage.classifiedSummary", { pest: state.triage_pest_name_snapshot || "—" })}
          </span>
          <Pencil className={cn("w-[18px] h-[18px] shrink-0", ICONO_COMPACTO_LG)} />
        </button>
        {/* Los dos en la misma fila SIEMPRE, también en móvil. Lo que cambia ahí es
            la etiqueta: «Tratamiento» y «Resuelto» en vez de las largas, que a media
            pantalla no caben. El icono se queda, que es lo que las hace reconocibles. */}
        <div className="grid grid-cols-2 gap-2 lg:gap-1.5">
          {state.linked_task_id && state.linked_task_id !== "pending" ? (
            <button
              type="button"
              onClick={() => navigate(`/planner?taskId=${state.linked_task_id}`)}
              className={cn(ACTION_CLASS, ACCION_COMPACTA_LG, "px-3 lg:px-2.5")}
              style={actionStyle(ACTION_COLORS.blue.bg, ACTION_COLORS.blue.glow)}
            >
              <ClipboardList className={cn("w-[18px] h-[18px] shrink-0", ICONO_COMPACTO_LG)} />
              <span className="truncate">{t("pests.scouting.triage.viewTask")}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreateTreatment}
              disabled={busy}
              className={cn(ACTION_CLASS, ACCION_COMPACTA_LG, "px-3 lg:px-2.5")}
              style={actionStyle(ACTION_COLORS.blue.bg, ACTION_COLORS.blue.glow)}
            >
              <Sparkles className={cn("w-[18px] h-[18px] shrink-0", ICONO_COMPACTO_LG)} />
              <span className="truncate sm:hidden">{t("pests.scouting.triage.createTreatmentShort", "Tratamiento")}</span>
              <span className="truncate hidden sm:inline">{t("pests.scouting.triage.createTreatment")}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleResolve}
            disabled={busy}
            className={cn(ACTION_CLASS, ACCION_COMPACTA_LG, "px-3 lg:px-2.5")}
            style={actionStyle(ACTION_COLORS.green.bg, ACTION_COLORS.green.glow)}
          >
            <Check className={cn("w-[18px] h-[18px] shrink-0", ICONO_COMPACTO_LG)} />
            <span className="truncate sm:hidden">{t("pests.scouting.triage.markResolvedShort", "Resuelto")}</span>
            <span className="truncate hidden sm:inline">{t("pests.scouting.triage.markResolved")}</span>
          </button>
        </div>
      </div>
    );
  } else if (state.triage_status === "dismissed") {
    content = (
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-muted/40 border">
        <div className="flex items-center gap-2 text-muted-foreground text-sm min-w-0">
          <XCircle className="w-[18px] h-[18px] shrink-0" />
          <span className="font-medium truncate">
            {t("pests.scouting.triage.dismissedSummary", {
              reason: t(`pests.scouting.triage.reasons.${state.dismissal_reason || "no_pest"}`),
            })}
          </span>
        </div>
        <button
          type="button"
          onClick={handleReopen}
          disabled={busy}
          className={cn(QUIET_ACTION_CLASS, "shrink-0")}
          style={QUIET_ACTION_STYLE}
        >
          <RotateCcw style={{ width: 19, height: 19, color: "#647E96" }} strokeWidth={2.2} />
          {t("pests.scouting.triage.reopen")}
        </button>
      </div>
    );
  } else {
    content = (
      <div className="grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={() => setClassifyOpen(true)}
          data-testid="triage-classify-open"
          className={cn(ACTION_CLASS, "px-3")}
          style={actionStyle(ACTION_COLORS.blue.bg, ACTION_COLORS.blue.glow)}
        >
          <CheckCircle2 className="w-[18px] h-[18px] shrink-0" />
          <span className="truncate">{t("pests.scouting.triage.classifyBtn")}</span>
        </button>
        <button
          type="button"
          onClick={() => setDismissOpen(true)}
          data-testid="triage-dismiss-open"
          className={cn(ACTION_CLASS, "px-3")}
          style={actionStyle(ACTION_COLORS.red.bg, ACTION_COLORS.red.glow)}
        >
          <XCircle className="w-[18px] h-[18px] shrink-0" />
          <span className="truncate">{t("pests.scouting.triage.dismissBtn")}</span>
        </button>
      </div>
    );
  }

  return (
    <>
      {workerResolvedAlert}
      {workerGoneAlert}
      {content}

      <Dialog open={classifyOpen} onOpenChange={setClassifyOpen}>
        {/* Con focos el diálogo crece (gravedad, foco, avance): tope de ventana centrada y el resto se lee bajando. */}
        <DialogContent className={cn("border border-[#b6dded] overflow-y-auto", SHEET_CENTERED_MAX_H_CLASS)}>
          <DialogHeader>
            <DialogTitle>{t("pests.scouting.triage.classifyTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Label>{t("pests.scouting.triage.selectPest")}</Label>
            {/* Ya clasificado: el desplegable se queda para ENSEÑAR qué plaga es,
                pero apagado. Sin el botón de clasificar no hay nada que aplique un
                cambio, y un desplegable que se puede mover sin que sirva de nada es
                peor que uno que no se mueve. */}
            <Select value={selectedPestId} onValueChange={setSelectedPestId} disabled={yaClasificado}>
              <SelectTrigger className="bg-white shadow-sm mt-1" data-testid="triage-pest-select">
                <SelectValue placeholder={t("pests.scouting.triage.selectPest")} />
              </SelectTrigger>
              <SelectContent>
                {pests.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{pestTranslation.getName(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pestFromVoice && !yaClasificado && (
              <p className="text-xs text-muted-foreground" data-testid="triage-voice-guess">
                {t("pests.scouting.triage.voiceGuess")}
              </p>
            )}
            {aiChip && (
              <AiSuggestionChip
                pestName={aiChip.name}
                confidence={aiChip.confidence}
                outsideList={aiChip.outsideList}
                picked={selectedPestId === aiChip.pestId}
                onPick={() => setSelectedPestId(aiChip.pestId)}
                disabled={busy}
                className="pt-1"
              />
            )}
            {withFoci && (
              <ConfirmSamplingFields
                form={form}
                className="pt-2"
                // «Cómo está» lo dice quien muestreó (D19): si el parte es de otro, aquí solo se lee.
                samplerIsMe={!sessionPlace.data?.inspectorId || sessionPlace.data.inspectorId === user?.id}
                worker={
                  sessionPlace.data && sessionPlace.data.inspectorId && sessionPlace.data.inspectorId !== user?.id
                    ? {
                        answer: sessionPlace.data.place.hint?.answer ?? sessionPlace.data.pendingHint?.answer ?? null,
                        verdict: sessionPlace.data.workerVerdict,
                        skipReason: sessionPlace.data.skipReason,
                        asked: !!sessionPlace.data.place.hint || !!sessionPlace.data.pendingHint,
                        pending: !sessionPlace.data.place.hint && !!sessionPlace.data.pendingHint,
                        focus: sessionPlace.data.hintFocus
                          ? { focusNumber: sessionPlace.data.hintFocus.focusNumber, pestName: sessionPlace.data.hintFocus.pestName, lineNumber: sessionPlace.data.hintFocus.lineNumber }
                          : null,
                      }
                    : null
                }
              />
            )}
          </div>
          {/* «Quitar clasificación» vive aquí desde que se sacó del panel. Va a la
              izquierda y en discreto para que no compita con la acción principal, y
              solo aparece si de verdad hay algo que quitar. */}
          {/* Sin «Cancelar»: la X de la esquina ya cierra el diálogo.
              flex-row también en móvil, cada botón a mitad de ancho, para que los dos
              quepan en la misma fila; el icono se esconde ahí, que es donde estorba. */}
          {/* UN SOLO BOTÓN CUANDO YA ESTÁ CLASIFICADO (usuario 05-09-2026). Salían
              los dos, «Quitar clasificación» y «Clasificar plaga», y el segundo no
              tenía sentido: la plaga YA está puesta, así que volver a clasificarla
              con la misma que ya muestra el desplegable no hace nada. Quien se haya
              equivocado quita la clasificación y vuelve a entrar; son dos toques en
              vez de uno, pero ninguno de los dos engaña. */}
          <DialogFooter className="flex-row gap-2 sm:space-x-0 sm:justify-between">
            {yaClasificado ? (
              <Button
                onClick={() => setUnclassifyOpen(true)}
                disabled={busy}
                data-testid="triage-unclassify"
                className="flex-1 text-white hover:brightness-95"
                style={{ backgroundColor: "#E5574E" }}
              >
                <RotateCcw className="hidden sm:inline-block w-3.5 h-3.5 sm:mr-1.5" />
                <span className="truncate">{t("pests.scouting.triage.unclassify")}</span>
              </Button>
            ) : (
              <Button
                onClick={withFoci ? handleConfirmWithFocus : handleClassify}
                /* Con focos no se confirma sin gravedad ni —si hay duda— sin elegir foco.
                   Mientras el parte aún se está leyendo tampoco: sin su sitio no hay propuesta. */
                disabled={busy || !selectedPestId || (withFoci && (!form.enabled || form.blocked !== null))}
                data-testid="confirm-save"
                data-blocked={withFoci ? form.blocked ?? "" : ""}
                className="flex-1 sm:flex-none sm:ml-auto"
              >
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                <span className="truncate">{withFoci ? t("pests.scouting.focusLink.confirm") : t("pests.scouting.triage.classifyBtn")}</span>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aviso al quitar la clasificación. Si ya hay tarea creada se avisa aparte:
          la tarea no se borra, pero deja de estar respaldada por una clasificación. */}
      <AlertDialog open={unclassifyOpen} onOpenChange={setUnclassifyOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("pests.scouting.triage.unclassifyConfirmTitle", "¿Quitar la clasificación?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {state.linked_task_id && state.linked_task_id !== "pending"
                ? t(
                    "pests.scouting.triage.unclassifyConfirmBodyWithTask",
                    "El reporte volverá a quedar pendiente de clasificar y perderá la plaga asignada. La tarea ya creada NO se borra, pero se quedará sin clasificación que la respalde.",
                  )
                : t(
                    "pests.scouting.triage.unclassifyConfirmBody",
                    "El reporte volverá a quedar pendiente de clasificar y perderá la plaga asignada. Podrás clasificarlo de nuevo cuando quieras.",
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleUnclassify(); }}
              disabled={busy}
              data-testid="triage-unclassify-ok"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {t("pests.scouting.triage.unclassify")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent className="border border-[#b6dded]">
          <DialogHeader>
            <DialogTitle>{t("pests.scouting.triage.dismissTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Label>{t("pests.scouting.triage.reasonLabel")}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`pests.scouting.triage.reasons.${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissOpen(false)} disabled={busy}>{t("common.cancel")}</Button>
            <Button onClick={handleDismiss} disabled={busy} variant="destructive">{t("pests.scouting.triage.dismissBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
